/**
 * LES HÉROS — vivants, morts, retirés.
 *
 * Les morts restent dans la liste, et ce n'est pas un oubli : un permadeath dont on efface les
 * traces n'est qu'une perte de temps. On les voit, on lit de quoi ils sont morts, et on recommence.
 */
import { useRessource } from "../api/chargement.js";
import type { HerosEnVue } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Filet } from "../composants/Parchemin.js";
import { ARCHETYPES } from "../lexique/mots.js";
import { Lien } from "../routeur.js";
import { t } from "../lexique/langue.js";

function Rangee({ heros }: { heros: HerosEnVue }) {
  const mort = heros.status === "dead";
  return (
    <Lien vers={`/heros/${heros.id}`} className={mort ? "carte-heros carte-heros--eteint" : "carte-heros"}>
      <span className="carte-heros__nom">
        {mort ? <Glyphe nom="crane" taille={16} couleur="var(--sang)" /> : null} {heros.nom}
      </span>
      <span className="capitales">{ARCHETYPES[heros.archetype]?.nom ?? heros.archetype}</span>
      <span className="rang rang--serre faible">
        <span className="nombre">
          {heros.pv} / {heros.pvMax}
        </span>
        <span>·</span>
        <span className="nombre" title={t("Indice de puissance de l'équipement — c'est ce qu'une aventure regarde à la porte.")}>
          {t("équipement")} {heros.ipe}
        </span>
        <span>·</span>
        {heros.status === "alive" ? t("Vivant") : heros.status === "dead" ? t("Mort") : t("Retiré")}
      </span>
    </Lien>
  );
}

export function LesHeros() {
  const { donnees } = useRessource<HerosEnVue[]>("/heros", (charge: { heros: HerosEnVue[] }) => charge.heros);
  const heros = donnees ?? [];
  const vivants = heros.filter((h) => h.status === "alive");
  const passes = heros.filter((h) => h.status !== "alive");

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("Vos héros")}</h1>
        <Lien vers="/heros/nouveau" className="bouton">
          {t("En créer un")}
        </Lien>
      </header>

      {vivants.length ? (
        <div className="grille grille--cartes">
          {vivants.map((h) => (
            <Rangee key={h.id} heros={h} />
          ))}
        </div>
      ) : (
        <p className="douce">{t("Personne de vivant, pour l'instant. Il faut bien que quelqu'un y aille.")}</p>
      )}

      {passes.length ? (
        <>
          <div style={{ marginTop: "var(--e7)" }}>
            <Filet>{t("Ceux d'avant")}</Filet>
          </div>
          <div className="grille grille--cartes" style={{ marginTop: "var(--e5)" }}>
            {passes.map((h) => (
              <Rangee key={h.id} heros={h} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
